import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { isCustomGroup, type IMailTemplateSummary } from '../../api/mail-template-contracts';
import { surveyLabel } from './template-fields';

/**
 * Which email is edited: a searchable list of every template, grouped "Survey emails", "Account
 * emails" and "Your templates", each with its state — not in use, a version
 * scheduled, the surveys with text of their own. The archived come last, folded under "Archived":
 * open while one of them is the email edited or a search is typed.
 */

export const GROUPS = [
  { group: 'survey', heading: 'Survey emails' },
  { group: 'account', heading: 'Account emails' },
  { group: 'custom', heading: 'Your templates' }
] as const;

/** A published version that no longer passes the checks: its mails go with the default. An
 *  unpublished staff template sends nothing, so it is not failing. */
export function notInUse(template: IMailTemplateSummary): boolean {
  return template.variants.some(
    (variant) => variant.valid === false && !(isCustomGroup(template.group) && variant.version === 0)
  );
}

/** "Not in use", "Scheduled", "Own text: FA, RIA" — what a template's variants say at a glance. */
function stateOf(template: IMailTemplateSummary): string[] {
  const base = template.variants.find((variant) => variant.survey === null);
  const state: string[] = [];
  if (template.archivedAt) state.push('Archived');
  if (notInUse(template)) state.push('Not in use');
  if (template.variants.some((variant) => variant.scheduledAt)) state.push('Scheduled');
  const own = template.variants.filter((variant) => variant.survey !== null && !variant.usesBase);
  if (own.length > 0) state.push(`Own text: ${own.map((variant) => surveyLabel(variant.survey)).join(', ')}`);
  if (state.length === 0 && base) {
    state.push(base.version === 0 ? (isCustomGroup(template.group) ? 'Not published' : 'Default text') : `Version ${base.version}`);
  }
  return state;
}

export function TemplatePicker({
  value,
  templates,
  unsaved = false,
  onSelect
}: {
  value: string;
  templates: readonly IMailTemplateSummary[];
  /** The open template holds edits not saved — `*` before its name, as Lists marks a view. */
  unsaved?: boolean;
  onSelect: (code: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const labelId = React.useId();
  const selected = templates.find((template) => template.code === value);
  const archived = templates.filter((template) => template.archivedAt);
  const editingArchived = Boolean(selected?.archivedAt);
  const [unfolded, setUnfolded] = React.useState(editingArchived);
  // Opened again when the email edited is archived while it is open.
  React.useEffect(() => {
    if (editingArchived) setUnfolded(true);
  }, [editingArchived]);
  const searching = search.trim() !== '';
  const show = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const option = (template: IMailTemplateSummary) => {
    const state = stateOf(template);
    const attention = state.includes('Not in use');
    return (
      <CommandItem
        key={template.code}
        value={template.code}
        keywords={[template.name, ...template.tags]}
        onSelect={() => {
          show(false);
          onSelect(template.code);
        }}
        aria-labelledby={`${labelId}-${template.code}-name ${labelId}-${template.code}-state`}
        data-template-option={template.code}
        className={cn(template.code === value && 'bg-[color-mix(in_srgb,var(--color-turquoise)_10%,white)]')}
      >
        <span className="grid min-w-0">
          <span id={`${labelId}-${template.code}-name`} className="truncate font-medium">
            {unsaved && template.code === value && <span aria-hidden>* </span>}
            {template.name}
          </span>
          <span
            id={`${labelId}-${template.code}-state`}
            className={cn(
              'truncate text-xs',
              attention ? 'text-[var(--color-deep-magenta)]' : 'text-muted-foreground'
            )}
          >
            {state.join(' · ')}
          </span>
        </span>
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={show}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          aria-label={`Email: ${selected?.name ?? value}${unsaved ? ', changed and not saved' : ''}`}
          className="-ml-2 h-8 min-w-0 justify-between gap-2 px-2 text-base font-semibold text-[var(--color-royal-blue)]"
          data-template-picker=""
        >
          <span className="truncate">
            {unsaved && <span aria-hidden>* </span>}
            {selected?.name ?? value}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <Command label="Emails" value={value}>
          <CommandInput placeholder="Find an email" value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[min(28rem,70dvh)] py-1">
            {GROUPS.map(({ group, heading }) => {
              const entries = templates.filter(
                (template) => !template.archivedAt && (template.group ?? 'survey') === group
              );
              if (entries.length === 0) return null;
              return (
                <CommandGroup key={group} heading={heading}>
                  {entries.map(option)}
                </CommandGroup>
              );
            })}
            {archived.length > 0 && (
              <>
                <CommandSeparator className="mx-0" />
                <CommandGroup heading="Archived" data-archived-group="">
                  {!searching && (
                    // Shown or hidden, never chosen: the list stays open.
                    <CommandItem
                      value="archived-fold"
                      onSelect={() => setUnfolded((was) => !was)}
                      aria-expanded={unfolded}
                      className="text-muted-foreground"
                      data-archived-fold=""
                    >
                      {unfolded ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                      {unfolded ? 'Hide archived' : `Show archived (${archived.length})`}
                    </CommandItem>
                  )}
                  {(unfolded || searching) && archived.map(option)}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
